import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Geometry } from 'geojson';
import axios from 'axios';
import config from 'constants/apiConfig';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from 'slices/commonSelectors';
import { EPA_GULF_ORG_EVENT_DEMO_KEY } from 'components/MapPage/CorporateViewTabs/NotificationsTab/epaGulfThreatNotification';
import { DHS_REGION4_ORG_EVENT_DEMO_KEY } from 'components/MapPage/CorporateViewTabs/NotificationsTab/dhsRegion4ThreatNotification';
import { EQUINOR_GRANE_ORG_EVENT_DEMO_KEY } from 'components/MapPage/CorporateViewTabs/NotificationsTab/equinorGraneSpillNotification';
import { ONTARIO_GTA_ORG_EVENT_DEMO_KEY } from 'components/MapPage/CorporateViewTabs/NotificationsTab/ontarioSnowstormNotification';
import { CNIC_HAWAII_ORG_EVENT_DEMO_KEY } from 'components/MapPage/CorporateViewTabs/NotificationsTab/cnicHawaiiThreatNotification';
import { TNNG_MEMPHIS_ORG_EVENT_DEMO_KEY } from 'components/MapPage/CorporateViewTabs/NotificationsTab/tennesseeMemphisThreatNotification';
import { USCG_FOC_SE_ORG_EVENT_DEMO_KEY } from 'components/MapPage/CorporateViewTabs/NotificationsTab/uscgFocSoutheastThreatNotification';
import type {
  OrgEventChecklistItemDef,
  OrgEventNotifyConfig,
} from './orgEventAutomationHooks';

export const CFA_HELENE_ORG_EVENT_DEMO_KEY = 'cfa-helene-cone';
export {
  EPA_GULF_ORG_EVENT_DEMO_KEY,
  DHS_REGION4_ORG_EVENT_DEMO_KEY,
  EQUINOR_GRANE_ORG_EVENT_DEMO_KEY,
  ONTARIO_GTA_ORG_EVENT_DEMO_KEY,
  CNIC_HAWAII_ORG_EVENT_DEMO_KEY,
  TNNG_MEMPHIS_ORG_EVENT_DEMO_KEY,
  USCG_FOC_SE_ORG_EVENT_DEMO_KEY,
};

export type OrgEventStatus = 'Active' | 'Monitoring' | 'Demobilizing';
export type OrgEventSeverity = 'High' | 'Medium' | 'Low';
export type OrgEventCreationKind = 'user' | 'threshold';

export type OrgEventChecklistNotifyTemplate = {
  notificationTypes?: string[];
  priority?: string;
  titleTemplate?: string;
  bodyTemplate?: string;
  emailSubject?: string | null;
  emailMessage?: string | null;
  smsMessage?: string | null;
  teamsMessage?: string | null;
  phoneMessage?: string | null;
  appMessage?: string | null;
  /** Prefill for the compose sheet; the automation never sends these itself. */
  userIds?: string[];
  teamIds?: string[];
};

export type OrgEventChecklistItemInstance =
  | {
      kind: 'org_action';
      actionId: string;
      label: string;
      /** Omitted = event-level item. */
      resourceSetId?: string;
    }
  | {
      kind: 'send_notification';
      id: string;
      label: string;
      notify?: OrgEventChecklistNotifyTemplate;
      sentNotificationId?: string;
      /** Omitted = event-level item. */
      resourceSetId?: string;
    };

export type OrgEvent = {
  id: string;
  group_guid: string;
  name: string;
  type: string | null;
  status: OrgEventStatus | string;
  severity: OrgEventSeverity | string;
  region: string | null;
  business_unit: string | null;
  lead: string | null;
  summary: string | null;
  resources_committed: string | null;
  lon: number | null;
  lat: number | null;
  geometry?: Geometry | null;
  started_at: string | null;
  last_update: string | null;
  creation_kind: OrgEventCreationKind | string;
  created_by_user_guid: string | null;
  created_by_name?: string | null;
  updated_by_user_guid?: string | null;
  updated_by_name?: string | null;
  threshold_description: string | null;
  demo_key: string | null;
  response_checklist_action_ids?: string[];
  response_checklist_items?: OrgEventChecklistItemInstance[];
  /** Union of resource set IDs that have matched this event; never shrinks. */
  impacted_resource_ids?: string[];
  /**
   * How much of the response checklist is done, resolved server-side. Actions
   * that have been archived are left out of the total, so this can shrink.
   */
  response_checklist_progress?: { total: number; complete: number };
  archived: boolean;
  created_at: string;
  updated_at: string;
};

export type CreateOrgEventPerAssetChecklist = {
  resourceSetId: string;
  name?: string;
  items: OrgEventChecklistItemDef[];
};

export type CreateOrgEventPayload = {
  group_guid: string;
  name: string;
  type?: string | null;
  status?: string;
  severity?: string;
  region?: string | null;
  business_unit?: string | null;
  lead?: string | null;
  summary?: string | null;
  resources_committed?: string | null;
  lon?: number | null;
  lat?: number | null;
  geometry?: Geometry | null;
  started_at?: string | null;
  creation_kind?: string;
  created_by_user_guid?: string | null;
  threshold_description?: string | null;
  demo_key?: string | null;
  response_checklist_action_ids?: string[];
  response_checklist_items?: OrgEventChecklistItemInstance[];
  impacted_resource_ids?: string[];
  notify?: OrgEventNotifyConfig;
  notify_config?: OrgEventNotifyConfig;
  checklist_items?: OrgEventChecklistItemDef[];
  per_asset_checklists?: CreateOrgEventPerAssetChecklist[];
};

export type UpdateOrgEventPayload = Partial<
  Omit<CreateOrgEventPayload, 'group_guid' | 'demo_key'>
> & {
  archived?: boolean;
};

export const orgEventKeys = {
  all: ['org-events'] as const,
  list: (groupGuid: string) =>
    [...orgEventKeys.all, 'list', groupGuid] as const,
  incidentRelated: (incidentId: string) =>
    [...orgEventKeys.all, 'incident-related', incidentId] as const,
};

export function useOrgEvents(options?: {
  enabled?: boolean;
  includeArchived?: boolean;
}) {
  const groupGuid = useSelector(selectGroupGuid);
  const includeArchived = options?.includeArchived ?? false;
  return useQuery({
    queryKey: [...orgEventKeys.list(groupGuid ?? ''), includeArchived],
    queryFn: async () => {
      const resp = await axios.get<OrgEvent[]>(config.apiGateway.org_events, {
        params: {
          groupGuid,
          ...(includeArchived ? { includeArchived: true } : {}),
        },
      });
      return resp.data ?? [];
    },
    enabled: !!groupGuid && (options?.enabled ?? true),
  });
}

export function useCreateOrgEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateOrgEventPayload) => {
      const resp = await axios.post<OrgEvent>(
        config.apiGateway.org_events,
        payload
      );
      return resp.data;
    },
    onSuccess: (event) => {
      void queryClient.invalidateQueries({
        queryKey: orgEventKeys.list(event.group_guid),
      });
    },
  });
}

export function useUpdateOrgEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: UpdateOrgEventPayload & { id: string }) => {
      const resp = await axios.patch<OrgEvent>(
        `${config.apiGateway.org_events}/${id}`,
        payload
      );
      return resp.data;
    },
    onSuccess: (event) => {
      void queryClient.invalidateQueries({
        queryKey: orgEventKeys.list(event.group_guid),
      });
    },
  });
}

export function useDeleteOrgEvent() {
  const queryClient = useQueryClient();
  const groupGuid = useSelector(selectGroupGuid);
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${config.apiGateway.org_events}/${id}`);
      return id;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orgEventKeys.list(groupGuid ?? ''),
      });
    },
  });
}

export function useSetIncidentRelatedEvents() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      incidentId,
      eventIds,
    }: {
      incidentId: string;
      eventIds: string[];
    }) => {
      const resp = await axios.put<OrgEvent[]>(
        `${config.apiGateway.org_events}/incident/${incidentId}/related`,
        { eventIds }
      );
      return resp.data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: orgEventKeys.incidentRelated(vars.incidentId),
      });
      void queryClient.invalidateQueries({ queryKey: ['incident'] });
    },
  });
}

export function findHeleneConeOrgEvent(
  events: OrgEvent[] | undefined
): OrgEvent | undefined {
  return (events ?? []).find(
    (event) => event.demo_key === CFA_HELENE_ORG_EVENT_DEMO_KEY
  );
}

export function findUscgFocSeOrgEvent(
  events: OrgEvent[] | undefined
): OrgEvent | undefined {
  return (events ?? []).find(
    (event) => event.demo_key === USCG_FOC_SE_ORG_EVENT_DEMO_KEY
  );
}

export function findEpaGulfOrgEvent(
  events: OrgEvent[] | undefined
): OrgEvent | undefined {
  return (events ?? []).find(
    (event) => event.demo_key === EPA_GULF_ORG_EVENT_DEMO_KEY
  );
}

export function findDhsRegion4OrgEvent(
  events: OrgEvent[] | undefined
): OrgEvent | undefined {
  return (events ?? []).find(
    (event) => event.demo_key === DHS_REGION4_ORG_EVENT_DEMO_KEY
  );
}

export function findEquinorGraneOrgEvent(
  events: OrgEvent[] | undefined
): OrgEvent | undefined {
  return (events ?? []).find(
    (event) => event.demo_key === EQUINOR_GRANE_ORG_EVENT_DEMO_KEY
  );
}

/** Resolves the seeded event for whichever facility demo is active. */
export function findFacilityDemoOrgEvent(
  events: OrgEvent[] | undefined,
  pack: { orgEventDemoKey: string }
): OrgEvent | undefined {
  return (events ?? []).find(
    (event) => event.demo_key === pack.orgEventDemoKey
  );
}

export function findTnngMemphisOrgEvent(
  events: OrgEvent[] | undefined
): OrgEvent | undefined {
  return (events ?? []).find(
    (event) => event.demo_key === TNNG_MEMPHIS_ORG_EVENT_DEMO_KEY
  );
}
